import React, { useState, useEffect } from "react";
import { Collapse, Typography, Row, Col, Tag } from "antd";
import moment from "moment";
import AppTable from "components/AppTable/AppTable";
import AnalyticsCard from "components/Admin/AnalyticsCard/AnalyticsCard";
import MonthFilterMenu from "components/Admin/FilterMenu/MonthFilterMenu";
import AppButton from "components/AppButton/AppButton";
import { inject, observer } from "mobx-react";
import { getPercentage } from "common/utils/commonUtilities";
import CreateInvoiceModal from "./UsageDetailModal";
import { fetchDedicatedNumbers, sendCreateInvoiceWithNumbers } from "service/adminService";
import UserDrawer from "./UserDrawer";

const getDedicatedNumberColumns = () => [
  {
    title: "Number",
    dataIndex: "number",
    width: 150,
    render: text => <span>{text || "-"}</span>
  },
  {
    title: "Date",
    dataIndex: "datetime",
    width: 150,
    render: date => <span>{date ? moment(date).format("DD/MM/YYYY") : "-"}</span>
  },
  {
    title: "Service Type",
    dataIndex: "serviceType",
    width: 120,
    render: serviceType => (serviceType ? <Tag color="blue">{serviceType.toUpperCase()}</Tag> : <span>-</span>)
  },
  {
    title: "Amount (GST Excluded)",
    dataIndex: "amount",
    width: 100,
    render: (amount, record) => (
      <span>
        {record.countryCode ? record.countryCode.toUpperCase() : ""}D {!isNaN(Number(amount)) && amount != null ? Number(amount).toFixed(2) / 100 : "-"}
      </span>
    )
  },
  {
    title: "Payment Mode",
    dataIndex: "payMode",
    width: 130,
    render: paymentMode => (paymentMode ? <Tag color="green">{paymentMode.toUpperCase()}</Tag> : <span>-</span>)
  },
  {
    title: "Invoice Status",
    dataIndex: "invoiceStatus",
    width: 120,
    render: invoiceStatus => <>{invoiceStatus ? "Completed" : "Pending"}</>
  },
  {
    title: "Created By",
    dataIndex: "createdBy",
    width: 120,
    render: createdBy => <span>{createdBy || "-"}</span>
  }
];

const getAdminColumns = () => [
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 100,
    render: text => <div style={{ fontSize: "12px" }}>{moment(text).format("DD/MM/YYYY HH:mm")}</div>
  },
  {
    title: "Total (SMS/MMS)",
    key: "sms_mms_price",
    width: 110,
    render: (_, record) => (
      <div style={{ fontSize: "12px" }}>
        SMS: {record.totalSmsCount || 0} <br />
        MMS: {record.totalMmsCount || 0}
      </div>
    )
  },
  {
    title: "Rate (SMS/MMS)",
    key: "rates",
    width: 110,
    render: (_, record) => (
      <div style={{ fontSize: "12px" }}>
        {(record.centsPerSMS / 100 || 0).toFixed(3)}¢ / {(record.centsPerMMS / 100 || 0).toFixed(3)}¢
      </div>
    )
  },
  {
    title: "Amount (GST Excluded)",
    dataIndex: "totalAmount",
    key: "amount",
    width: 100,
    render: (text, record) => (
      <div style={{ fontSize: "12px", fontWeight: "500" }}>
        {record.currency?.code?.toUpperCase() || ""} {(text / 100).toFixed(2)}
      </div>
    )
  },
  {
    title: "Payment Mode",
    dataIndex: "paymentMode",
    key: "payment_mode",
    width: 120,
    render: text => {
      if (!text) return <span style={{ fontSize: "12px" }}>-</span>;

      const mode = text.toLowerCase();
      let color = "gray";

      if (mode === "reward") {
        color = "gold";
      } else if (mode === "stripe") {
        color = "blue";
      }

      return (
        <Tag color={color} style={{ fontSize: "11px" }}>
          {text.toUpperCase()}
        </Tag>
      );
    }
  },
  {
    title: "Invoicing Type",
    dataIndex: "invoiceMethod",
    key: "invoice_method",
    width: 120,
    render: text => {
      if (!text) return <span style={{ fontSize: "12px" }}>-</span>;

      const method = text.toLowerCase();
      const isUsage = method === "usage" || method === "usages";
      const isCredit = method === "credit" || method === "credits";

      if (isUsage || isCredit) {
        return (
          <Tag color={isUsage ? "orange" : "green"} style={{ fontSize: "11px" }}>
            {isUsage ? "USAGE" : "CREDIT"}
          </Tag>
        );
      }

      return (
        <Tag color="default" style={{ fontSize: "11px" }}>
          {text.toUpperCase()}
        </Tag>
      );
    }
  },
  {
    title: "Invoice Status",
    dataIndex: "invoiceStatus",
    key: "invoiceStatus",
    width: 120,
    render: invoiceStatus => <>{invoiceStatus ? "Completed" : "Pending"}</>
  }
];

function AccordionDashboard({ adminGatewayStore, match }) {
  const { getTotalSystemUsage, getUserPurchaseOrders, getUserSenderIds, getTotalSystemPurchase } = adminGatewayStore;

  const { userId } = match.params;

  const [activeKeys, setActiveKeys] = useState(["2"]);
  const [month, setMonth] = useState(moment().month() + 1);
  const [year, setYear] = useState(moment().format("YYYY"));
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [adminTableLoading, setAdminTableLoading] = useState(false);
  const [dedicatedNumbersLoading, setDedicatedNumbersLoading] = useState(false);
  const [purchaseData, setPurchaseData] = useState([]);
  const [oldPurchaseData, setOldPurchaseData] = useState([]);
  const [dedicatedNumbers, setDedicatedNumbers] = useState([]);
  const [visibleDrawer, setVisibleDrawer] = useState(false);
  // API data states
  const [smsStatusData, setSmsStatusData] = useState(null);
  const [oldSmsStatusData, setOldSmsStatusData] = useState(null);
  const [mmsStatusData, setMmsStatusData] = useState(null);
  const [oldMmsStatusData, setOldMmsStatusData] = useState(null);

  // Fetch data from API
  useEffect(() => {
    if (userId) {
      setLoading(true);
      getTotalSystemPurchase({ month, year }, userId)
        .then(res => {
          if (res.status) {
            const { currentMonthData, previousMonthData } = res.data;
            setPurchaseData(currentMonthData);
            setOldPurchaseData(previousMonthData);
          }
        })
        .finally(() => setLoading(false));
      getTotalSystemUsage({ month, year }, userId)
        .then(res => {
          if (res.status) {
            const { currentSmsData, previousSmsData } = res.data;
            setSmsStatusData(currentSmsData.smsData[0]);
            setOldSmsStatusData(previousSmsData.smsData[0]);
            setMmsStatusData(currentSmsData.mmsData[0]);
            setOldMmsStatusData(previousSmsData.mmsData[0]);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [userId, year, month, getTotalSystemUsage]);

  // Fetch purchase orders for admin table
  useEffect(() => {
    if (userId) {
      setAdminTableLoading(true);
      getUserPurchaseOrders({ userId, type: "usage", month, year })
        .then(() => {
          // Data will be available in adminGatewayStore.userPurchaseOrdersList
        })
        .finally(() => setAdminTableLoading(false));
    }
  }, [month, year, userId, getUserPurchaseOrders]);

  // Fetch dedicated numbers for virtual number table
  useEffect(() => {
    if (userId) {
      setDedicatedNumbersLoading(true);
      getUserSenderIds({ userId, type: "usage", month, year })
        .then(() => {
          // Data will be available in adminGatewayStore.userSenderIdsList
        })
        .finally(() => setDedicatedNumbersLoading(false));
    }
  }, [month, year, userId, getUserSenderIds]);

  const handleCollapseChange = keys => {
    setActiveKeys(Array.isArray(keys) ? keys : [keys]);
  };

  // Helper function to get purchase data by type
  const getPurchaseDataByType = type => {
    const current = purchaseData.find(item => item._id === type);
    const previous = oldPurchaseData.find(item => item._id === type);

    const currentAmount = current ? (current.totalPurchases / 100).toFixed(2) : "0.00";
    const percentage = previous ? getPercentage(current?.totalPurchases || 0, previous.totalPurchases) : 0;

    return {
      purchase: `A$ ${currentAmount}`,
      graph: `${percentage > 0 ? "+" : ""}${percentage.toFixed(2)}%`
    };
  };

  // Generate analytics data from API response
  const analyticsData = [
    {
      id: "1",
      type: "sent",
      label: "Total SMS Sent",
      value: smsStatusData ? Number(smsStatusData?.totalSMS) - Number(smsStatusData?.totalSMSReceived) - Number(smsStatusData?.totalSMSBlocked) : 0,
      icon: <i className="fas fa-paper-plane"></i>,
      cost: false,
      percentage: smsStatusData
        ? getPercentage(
            Number(smsStatusData?.totalSMS) - Number(smsStatusData?.totalSMSReceived) - Number(smsStatusData?.totalSMSBlocked),
            Number(oldSmsStatusData?.totalSMS) - Number(oldSmsStatusData?.totalSMSReceived) - Number(oldSmsStatusData?.totalSMSBlocked)
          )
        : 0
    },
    {
      id: "2",
      type: "coin",
      label: "Total Spent on SMS",
      value: smsStatusData ? ((smsStatusData?.totalSpent || 0) / 100).toFixed(3) : 0,
      icon: <i className="fas fa-coins"></i>,
      cost: true,
      percentage: smsStatusData ? getPercentage(((smsStatusData?.totalSpent || 0) / 100).toFixed(3), ((oldSmsStatusData?.totalSpent || 0) / 100).toFixed(3)) : 0
    },
    {
      id: "3",
      type: "sent",
      label: "Total MMS Sent",
      value: mmsStatusData ? Number(mmsStatusData?.totalSMS) - Number(mmsStatusData?.totalSMSReceived) - Number(mmsStatusData?.totalSMSBlocked) : 0,
      icon: <i className="fas fa-paper-plane"></i>,
      cost: false,
      percentage: mmsStatusData
        ? getPercentage(
            Number(mmsStatusData?.totalSMS) - Number(mmsStatusData?.totalSMSReceived) - Number(mmsStatusData?.totalSMSBlocked),
            Number(oldMmsStatusData?.totalSMS) - Number(oldMmsStatusData?.totalSMSReceived) - Number(oldMmsStatusData?.totalSMSBlocked)
          )
        : 0
    },
    {
      id: "4",
      type: "coin",
      label: "Total Spent on MMS",
      value: mmsStatusData ? ((mmsStatusData?.totalSpent || 0) / 100).toFixed(3) : 0,
      icon: <i className="fas fa-coins"></i>,
      cost: true,
      percentage: mmsStatusData ? getPercentage(((mmsStatusData?.totalSpent || 0) / 100).toFixed(3), ((oldMmsStatusData?.totalSpent || 0) / 100).toFixed(3)) : 0
    }
  ];

  // Pagination config for Purchase Orders (Admin table)
  const purchaseOrdersPaginationConfig = {
    defaultCurrent: 1,
    current: adminGatewayStore.purchaseOrdersPagination.current,
    total: adminGatewayStore.purchaseOrdersPagination.total,
    pageSize: adminGatewayStore.purchaseOrdersPagination.pageSize,
    onChange: current => adminGatewayStore.setPurchaseOrdersPage(current),
    showSizeChanger: true,
    onShowSizeChange: (current, pageSize) => adminGatewayStore.setPurchaseOrdersPageSize(pageSize),
    className: "float-start",
    hideOnSinglePage: true,
    showQuickJumper: true
  };

  // Pagination config for Sender IDs (Virtual Number table)
  const senderIdsPaginationConfig = {
    defaultCurrent: 1,
    current: adminGatewayStore.senderIdsPagination.current,
    total: adminGatewayStore.senderIdsPagination.total,
    pageSize: adminGatewayStore.senderIdsPagination.pageSize,
    onChange: current => adminGatewayStore.setSenderIdsPage(current),
    showSizeChanger: true,
    onShowSizeChange: (current, pageSize) => adminGatewayStore.setSenderIdsPageSize(pageSize),
    className: "float-start",
    hideOnSinglePage: true,
    showQuickJumper: true
  };

  const adminData = getPurchaseDataByType("ADMIN");

  const accordionData = [
    {
      key: "1",
      title: "VIRTUAL_NUMBER",
      disabled: false,
      showArrow: true,
      showStats: false,
      content: (
        <AppTable
          loading={dedicatedNumbersLoading}
          columns={getDedicatedNumberColumns()}
          dataSource={adminGatewayStore.userSenderIdsUsageList}
          rowKey={record => record._id || record.id}
          pagination={senderIdsPaginationConfig}
          bordered
          scroll={{ x: 750 }}
        />
      )
    },
    {
      key: "2",
      title: "ADMIN",
      purchase: adminData.purchase,
      graph: adminData.graph,
      disabled: false,
      showArrow: true,
      content: (
        <AppTable
          loading={adminTableLoading}
          columns={getAdminColumns()}
          dataSource={adminGatewayStore.userPurchaseOrdersList || []}
          pagination={purchaseOrdersPaginationConfig}
          bordered
          rowKey={record => record._id}
          scroll={{ x: 750 }}
        />
      )
    }
  ];

  const handleModalOpen = () => {
    setIsModalVisible(true);
  };

  const handleCreateInvoice = async formValues => {
    try {
      // Prepare API parameters
      const apiParams = {
        month: month,
        year: year,
        userId: userId,
        medium: "admin",
        ...formValues
      };

      await sendCreateInvoiceWithNumbers(apiParams);
      setIsModalVisible(false);
    } catch (e) {
      console.error("Error creating invoice:", e);
    }
  };

  const getDedicatedNumbersForModal = async () => {
    try {
      const data = await fetchDedicatedNumbers({
        page: 0,
        limit: 0,
        userId: userId,
        type: "usage"
      });

      const filteredData = data.data.senderIds.map(item => ({
        phone: item.number,
        amount: item.amount ? Number(item.amount) : 0
      }));

      setDedicatedNumbers(filteredData);
      return filteredData;
    } catch (error) {
      console.error("Failed to fetch dedicated numbers", error);
      return [];
    }
  };

  const getFirstRowRates = () => {
    const firstRow = adminGatewayStore.userPurchaseOrdersList?.[0];
    if (!firstRow) return { rateSms: 0, rateMms: 0 };

    return {
      rateSms: firstRow.centsPerSMS / 100 || 0,
      rateMms: firstRow.centsPerMMS / 100 || 0
    };
  };

  const { rateSms, rateMms } = getFirstRowRates();
  useEffect(() => {
    getDedicatedNumbersForModal();
  }, [userId]);

  return (
    <div className="my-5 px-3">
      {/* Header with user email */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Typography.Title level={4}>Admin Usage Detail</Typography.Title>

        <div className="d-flex align-items-center">
          <MonthFilterMenu
            month={month}
            year={year}
            setMonth={setMonth}
            setYear={setYear}
            showExportButton={false}
            span={{
              xs: 24,
              sm: 12,
              md: 8,
              lg: 6,
              xl: 4
            }}
          />

          <AppButton type="primary" size="default" onClick={handleModalOpen} label="Create Invoice" light style={{ height: "32px" }} />
          <AppButton type="primary" size="default" onClick={() => setVisibleDrawer(true)} label="View Invoice" light style={{ height: "32px", marginLeft: "16px" }} />
          <UserDrawer
            visible={visibleDrawer}
            activeDrawer="viewInvoice"
            onClose={() => {
              setVisibleDrawer(false);
            }}
            userId={userId}
          />
        </div>
      </div>

      {/* Accordion */}
      <div className="my-4">
        <Collapse activeKey={activeKeys} onChange={handleCollapseChange}>
          {accordionData.map(item => (
            <Collapse.Panel
              key={item.key}
              disabled={item.disabled}
              showArrow={item.showArrow}
              header={
                <div className="d-flex justify-content-between align-items-center pe-4 w-100">
                  <Typography.Text strong>{item.title}</Typography.Text>
                  {item.showStats !== false && (
                    <div className="d-flex gap-4">
                      <div className="text-center">
                        <div className="text-muted small">Purchase</div>
                        <strong>{item.purchase}</strong>
                      </div>
                      <div className="text-center">
                        <div className="text-muted small">Graph</div>
                        <Tag color={item.graph.includes("+") ? "green" : item.graph.includes("-") ? "red" : "cyan"}>{item.graph}</Tag>
                      </div>
                    </div>
                  )}
                </div>
              }
            >
              {item.content}
            </Collapse.Panel>
          ))}
        </Collapse>
      </div>

      {/* Analytics Cards */}
      <Row gutter={[16, 16]} className=" mt-4">
        {analyticsData.map((analytics, index) => (
          <Col xs={24} sm={12} md={12} lg={12} xl={12} key={analytics.id}>
            <AnalyticsCard
              id={analytics.id}
              type={analytics.type}
              label={analytics.label}
              value={analytics.value}
              icon={analytics.icon}
              cost={analytics.cost}
              percentage={analytics.percentage}
              loading={loading}
            />
          </Col>
        ))}
      </Row>

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isModalVisible={isModalVisible}
        handleModalClose={() => setIsModalVisible(false)}
        onSubmit={handleCreateInvoice}
        dedicatedNumbers={dedicatedNumbers}
        totalSms={smsStatusData ? Number(smsStatusData?.totalSMS) - Number(smsStatusData?.totalSMSReceived) - Number(smsStatusData?.totalSMSBlocked) : 0}
        totalMms={mmsStatusData ? mmsStatusData?.totalSMS - mmsStatusData?.totalSMSReceived - mmsStatusData?.totalSMSBlocked : 0}
        rateSms={rateSms}
        rateMms={rateMms}
      />
    </div>
  );
}

export default inject(stores => ({
  adminGatewayStore: stores.store.adminGatewayStore
}))(observer(AccordionDashboard));
